import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Edit3,
  Trash2,
  X,
  Save,
  Loader2,
  AlertCircle,
  MapPin,
  PlaneTakeoff,
  Link2,
  Bus,
  Building2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { apiGet, apiPost, apiPut, apiDelete } from '@/services/api';
import { API_ROUTES, Role } from '@avgrp/shared';
import { useAuth } from '@/hooks/useAuth';
import { hasMinRole } from '@/utils/roles';

// ─── Types ──────────────────────────────────────────────────

interface ParkingStandRef {
  id: string;
  name: string;
  isRemote: boolean;
  isActive: boolean;
}

interface GateRef {
  id: string;
  name: string;
  terminal: string | null;
  isActive: boolean;
}

interface GateParkingMapping {
  id: string;
  gateId: string;
  parkingStandId: string;
  isDefault: boolean;
  parkingStand?: ParkingStandRef;
  gate?: GateRef;
}

interface Gate {
  id: string;
  name: string;
  terminal: string | null;
  isActive: boolean;
  createdAt: string;
  parkingMappings: (GateParkingMapping & { parkingStand: ParkingStandRef })[];
}

interface ParkingStand {
  id: string;
  name: string;
  isRemote: boolean;
  isActive: boolean;
  createdAt: string;
  gateMappings: (GateParkingMapping & { gate: GateRef })[];
}

interface Airport {
  id: string;
  iataCode: string;
  icaoCode: string;
  name: string;
  city: string;
  country: string;
  timezone: string;
  latitude: number | null;
  longitude: number | null;
  gates: Gate[];
  parkingStands: ParkingStand[];
}

// ─── Component ──────────────────────────────────────────────

export function AirportPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canEdit = hasMinRole(user?.role, Role.SUPERVISOR);

  // Active tab
  const [activeTab, setActiveTab] = useState<'info' | 'gates' | 'stands' | 'mappings'>('info');

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Gate form
  const [showGateForm, setShowGateForm] = useState(false);
  const [editingGateId, setEditingGateId] = useState<string | null>(null);
  const [gateForm, setGateForm] = useState({ name: '', terminal: '', isActive: true });

  // Stand form
  const [showStandForm, setShowStandForm] = useState(false);
  const [editingStandId, setEditingStandId] = useState<string | null>(null);
  const [standForm, setStandForm] = useState({ name: '', isRemote: false, isActive: true });

  // Mapping form
  const [showMappingForm, setShowMappingForm] = useState(false);
  const [mappingForm, setMappingForm] = useState({ gateId: '', parkingStandId: '', isDefault: false });

  // Airport info form
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({ name: '', city: '', country: '', timezone: '' });

  // Expanded gate/stand rows
  const [expandedGateId, setExpandedGateId] = useState<string | null>(null);
  const [expandedStandId, setExpandedStandId] = useState<string | null>(null);

  const [formError, setFormError] = useState('');

  // ─── Query ─────────────────────────────────────────────────
  const { data: airport, isLoading } = useQuery({
    queryKey: ['airport'],
    queryFn: () => apiGet<{ data: Airport }>(API_ROUTES.AIRPORTS),
    select: (res) => res.data,
  });

  // ─── Mutations ─────────────────────────────────────────────

  const updateAirportMut = useMutation({
    mutationFn: (data: Partial<typeof infoForm>) => apiPut(API_ROUTES.AIRPORTS, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['airport'] }); setEditingInfo(false); setFormError(''); },
    onError: (e: any) => setFormError(e?.response?.data?.error?.message || e?.message || 'Failed'),
  });

  const createGateMut = useMutation({
    mutationFn: (data: typeof gateForm) => apiPost(`${API_ROUTES.AIRPORTS}/gates`, { ...data, terminal: data.terminal || null }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['airport'] }); resetGateForm(); },
    onError: (e: any) => setFormError(e?.response?.data?.error?.message || e?.message || 'Failed'),
  });

  const updateGateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof gateForm }) => apiPut(`${API_ROUTES.AIRPORTS}/gates/${id}`, { ...data, terminal: data.terminal || null }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['airport'] }); resetGateForm(); },
    onError: (e: any) => setFormError(e?.response?.data?.error?.message || e?.message || 'Failed'),
  });

  const deleteGateMut = useMutation({
    mutationFn: (id: string) => apiDelete(`${API_ROUTES.AIRPORTS}/gates/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['airport'] }),
  });

  const createStandMut = useMutation({
    mutationFn: (data: typeof standForm) => apiPost(`${API_ROUTES.AIRPORTS}/stands`, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['airport'] }); resetStandForm(); },
    onError: (e: any) => setFormError(e?.response?.data?.error?.message || e?.message || 'Failed'),
  });

  const updateStandMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof standForm }) => apiPut(`${API_ROUTES.AIRPORTS}/stands/${id}`, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['airport'] }); resetStandForm(); },
    onError: (e: any) => setFormError(e?.response?.data?.error?.message || e?.message || 'Failed'),
  });

  const deleteStandMut = useMutation({
    mutationFn: (id: string) => apiDelete(`${API_ROUTES.AIRPORTS}/stands/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['airport'] }),
  });

  const createMappingMut = useMutation({
    mutationFn: (data: typeof mappingForm) => apiPost(`${API_ROUTES.AIRPORTS}/mappings`, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['airport'] }); resetMappingForm(); },
    onError: (e: any) => setFormError(e?.response?.data?.error?.message || e?.message || 'Failed'),
  });

  const deleteMappingMut = useMutation({
    mutationFn: (id: string) => apiDelete(`${API_ROUTES.AIRPORTS}/mappings/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['airport'] }),
  });

  // ─── Reset helpers ────────────────────────────────────────

  function resetGateForm() {
    setShowGateForm(false);
    setEditingGateId(null);
    setGateForm({ name: '', terminal: '', isActive: true });
    setFormError('');
  }

  function resetStandForm() {
    setShowStandForm(false);
    setEditingStandId(null);
    setStandForm({ name: '', isRemote: false, isActive: true });
    setFormError('');
  }

  function resetMappingForm() {
    setShowMappingForm(false);
    setMappingForm({ gateId: '', parkingStandId: '', isDefault: false });
    setFormError('');
  }

  // ─── Filter helpers ────────────────────────────────────────

  const filteredGates = airport?.gates.filter((g) =>
    g.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (g.terminal?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false)
  ) ?? [];

  const filteredStands = airport?.parkingStands.filter((s) =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase())
  ) ?? [];

  // ─── Loading / Error ─────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-aviation-600" />
      </div>
    );
  }

  if (!airport) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
          <p className="mt-2 text-gray-600">Could not load airport data</p>
        </div>
      </div>
    );
  }

  // ─── Tabs ─────────────────────────────────────────────────

  const tabs = [
    { key: 'info' as const, label: 'Airport Info', icon: Building2 },
    { key: 'gates' as const, label: `Gates (${airport.gates.length})`, icon: PlaneTakeoff },
    { key: 'stands' as const, label: `Parking Stands (${airport.parkingStands.length})`, icon: MapPin },
    { key: 'mappings' as const, label: 'Gate-Stand Mapping', icon: Link2 },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Airport Management — {airport.iataCode}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {airport.name} · {airport.city}, {airport.country}
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-0" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setSearchQuery(''); setFormError(''); }}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'border-aviation-600 text-aviation-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Error banner */}
      {formError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <span className="text-sm text-red-700">{formError}</span>
            <button onClick={() => setFormError('')} className="ml-auto text-red-400 hover:text-red-600">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* ═══════ Airport Info Tab ═══════ */}
      {activeTab === 'info' && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">Airport Information</h2>
            {canEdit && !editingInfo && (
              <button
                onClick={() => {
                  setInfoForm({ name: airport.name, city: airport.city, country: airport.country, timezone: airport.timezone });
                  setEditingInfo(true);
                }}
                className="flex items-center gap-1.5 rounded-lg bg-aviation-50 px-3 py-1.5 text-sm font-medium text-aviation-700 hover:bg-aviation-100"
              >
                <Edit3 className="h-3.5 w-3.5" /> Edit
              </button>
            )}
          </div>

          <div className="p-6">
            {editingInfo ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Airport Name</label>
                    <input
                      value={infoForm.name}
                      onChange={(e) => setInfoForm({ ...infoForm, name: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-aviation-500 focus:ring-1 focus:ring-aviation-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">City</label>
                    <input
                      value={infoForm.city}
                      onChange={(e) => setInfoForm({ ...infoForm, city: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-aviation-500 focus:ring-1 focus:ring-aviation-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Country</label>
                    <input
                      value={infoForm.country}
                      onChange={(e) => setInfoForm({ ...infoForm, country: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-aviation-500 focus:ring-1 focus:ring-aviation-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Timezone</label>
                    <input
                      value={infoForm.timezone}
                      onChange={(e) => setInfoForm({ ...infoForm, timezone: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-aviation-500 focus:ring-1 focus:ring-aviation-500"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => updateAirportMut.mutate(infoForm)}
                    disabled={updateAirportMut.isPending}
                    className="flex items-center gap-1.5 rounded-lg bg-aviation-600 px-4 py-2 text-sm font-medium text-white hover:bg-aviation-700 disabled:opacity-50"
                  >
                    {updateAirportMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save
                  </button>
                  <button onClick={() => { setEditingInfo(false); setFormError(''); }} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
                <InfoItem label="IATA Code" value={airport.iataCode} />
                <InfoItem label="ICAO Code" value={airport.icaoCode} />
                <InfoItem label="Name" value={airport.name} />
                <InfoItem label="City" value={airport.city} />
                <InfoItem label="Country" value={airport.country} />
                <InfoItem label="Timezone" value={airport.timezone} />
                <InfoItem label="Latitude" value={airport.latitude?.toFixed(4) ?? '—'} />
                <InfoItem label="Longitude" value={airport.longitude?.toFixed(4) ?? '—'} />
                <InfoItem label="Gates" value={String(airport.gates.length)} />
                <InfoItem label="Parking Stands" value={String(airport.parkingStands.length)} />
                <InfoItem label="Remote Stands" value={String(airport.parkingStands.filter((s) => s.isRemote).length)} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════ Gates Tab ═══════ */}
      {activeTab === 'gates' && (
        <div className="space-y-4">
          {/* Search + Add */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search gates..."
                className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-aviation-500 focus:ring-1 focus:ring-aviation-500"
              />
            </div>
            {canEdit && (
              <button
                onClick={() => { resetGateForm(); setShowGateForm(true); }}
                className="flex items-center gap-1.5 rounded-lg bg-aviation-600 px-4 py-2 text-sm font-medium text-white hover:bg-aviation-700"
              >
                <Plus className="h-4 w-4" /> Add Gate
              </button>
            )}
          </div>

          {/* Create/Edit Gate Form */}
          {showGateForm && (
            <div className="rounded-xl border border-aviation-200 bg-aviation-50 p-4">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">
                {editingGateId ? 'Edit Gate' : 'Add New Gate'}
              </h3>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Gate Name</label>
                  <input
                    value={gateForm.name}
                    onChange={(e) => setGateForm({ ...gateForm, name: e.target.value })}
                    placeholder="e.g. A5, B10"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-aviation-500 focus:ring-1 focus:ring-aviation-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Terminal</label>
                  <input
                    value={gateForm.terminal}
                    onChange={(e) => setGateForm({ ...gateForm, terminal: e.target.value })}
                    placeholder="e.g. Domestic, International"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-aviation-500 focus:ring-1 focus:ring-aviation-500"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={gateForm.isActive}
                    onChange={(e) => setGateForm({ ...gateForm, isActive: e.target.checked })}
                    className="rounded border-gray-300 text-aviation-600"
                  />
                  Active
                </label>
                <button
                  onClick={() => {
                    if (!gateForm.name.trim()) { setFormError('Gate name is required'); return; }
                    if (editingGateId) {
                      updateGateMut.mutate({ id: editingGateId, data: gateForm });
                    } else {
                      createGateMut.mutate(gateForm);
                    }
                  }}
                  disabled={createGateMut.isPending || updateGateMut.isPending}
                  className="flex items-center gap-1.5 rounded-lg bg-aviation-600 px-4 py-2 text-sm font-medium text-white hover:bg-aviation-700 disabled:opacity-50"
                >
                  {(createGateMut.isPending || updateGateMut.isPending) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {editingGateId ? 'Update' : 'Add'}
                </button>
                <button onClick={resetGateForm} className="rounded-lg border border-gray-300 p-2 text-gray-500 hover:bg-gray-50">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Gate List */}
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium text-gray-600">Gate</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Terminal</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Mapped Stands</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Status</th>
                  {canEdit && <th className="px-4 py-3 font-medium text-gray-600 w-24">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredGates.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No gates found</td></tr>
                )}
                {filteredGates.map((gate) => (
                  <GateRow
                    key={gate.id}
                    gate={gate}
                    canEdit={canEdit}
                    isExpanded={expandedGateId === gate.id}
                    onToggle={() => setExpandedGateId(expandedGateId === gate.id ? null : gate.id)}
                    onEdit={() => {
                      setGateForm({ name: gate.name, terminal: gate.terminal ?? '', isActive: gate.isActive });
                      setEditingGateId(gate.id);
                      setShowGateForm(true);
                      setFormError('');
                    }}
                    onDelete={() => deleteGateMut.mutate(gate.id)}
                    onDeleteMapping={(id: string) => deleteMappingMut.mutate(id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══════ Parking Stands Tab ═══════ */}
      {activeTab === 'stands' && (
        <div className="space-y-4">
          {/* Search + Add */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search parking stands..."
                className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-aviation-500 focus:ring-1 focus:ring-aviation-500"
              />
            </div>
            {canEdit && (
              <button
                onClick={() => { resetStandForm(); setShowStandForm(true); }}
                className="flex items-center gap-1.5 rounded-lg bg-aviation-600 px-4 py-2 text-sm font-medium text-white hover:bg-aviation-700"
              >
                <Plus className="h-4 w-4" /> Add Stand
              </button>
            )}
          </div>

          {/* Create/Edit Stand Form */}
          {showStandForm && (
            <div className="rounded-xl border border-aviation-200 bg-aviation-50 p-4">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">
                {editingStandId ? 'Edit Parking Stand' : 'Add New Parking Stand'}
              </h3>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Stand Name</label>
                  <input
                    value={standForm.name}
                    onChange={(e) => setStandForm({ ...standForm, name: e.target.value })}
                    placeholder="e.g. Stand 15, Remote 4"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-aviation-500 focus:ring-1 focus:ring-aviation-500"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={standForm.isRemote}
                    onChange={(e) => setStandForm({ ...standForm, isRemote: e.target.checked })}
                    className="rounded border-gray-300 text-orange-600"
                  />
                  <span className="flex items-center gap-1">
                    <Bus className="h-3.5 w-3.5 text-orange-500" />
                    Remote
                  </span>
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={standForm.isActive}
                    onChange={(e) => setStandForm({ ...standForm, isActive: e.target.checked })}
                    className="rounded border-gray-300 text-aviation-600"
                  />
                  Active
                </label>
                <button
                  onClick={() => {
                    if (!standForm.name.trim()) { setFormError('Stand name is required'); return; }
                    if (editingStandId) {
                      updateStandMut.mutate({ id: editingStandId, data: standForm });
                    } else {
                      createStandMut.mutate(standForm);
                    }
                  }}
                  disabled={createStandMut.isPending || updateStandMut.isPending}
                  className="flex items-center gap-1.5 rounded-lg bg-aviation-600 px-4 py-2 text-sm font-medium text-white hover:bg-aviation-700 disabled:opacity-50"
                >
                  {(createStandMut.isPending || updateStandMut.isPending) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {editingStandId ? 'Update' : 'Add'}
                </button>
                <button onClick={resetStandForm} className="rounded-lg border border-gray-300 p-2 text-gray-500 hover:bg-gray-50">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Stand List */}
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium text-gray-600">Stand</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Type</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Mapped Gates</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Status</th>
                  {canEdit && <th className="px-4 py-3 font-medium text-gray-600 w-24">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredStands.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No parking stands found</td></tr>
                )}
                {filteredStands.map((stand) => (
                  <StandRow
                    key={stand.id}
                    stand={stand}
                    canEdit={canEdit}
                    isExpanded={expandedStandId === stand.id}
                    onToggle={() => setExpandedStandId(expandedStandId === stand.id ? null : stand.id)}
                    onEdit={() => {
                      setStandForm({ name: stand.name, isRemote: stand.isRemote, isActive: stand.isActive });
                      setEditingStandId(stand.id);
                      setShowStandForm(true);
                      setFormError('');
                    }}
                    onDelete={() => deleteStandMut.mutate(stand.id)}
                    onDeleteMapping={(id: string) => deleteMappingMut.mutate(id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══════ Mappings Tab ═══════ */}
      {activeTab === 'mappings' && (
        <div className="space-y-4">
          {/* Add Mapping */}
          {canEdit && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => { resetMappingForm(); setShowMappingForm(true); }}
                className="flex items-center gap-1.5 rounded-lg bg-aviation-600 px-4 py-2 text-sm font-medium text-white hover:bg-aviation-700"
              >
                <Plus className="h-4 w-4" /> Add Mapping
              </button>
            </div>
          )}

          {/* Mapping Form */}
          {showMappingForm && (
            <div className="rounded-xl border border-aviation-200 bg-aviation-50 p-4">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">Create Gate → Stand Mapping</h3>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Gate</label>
                  <select
                    value={mappingForm.gateId}
                    onChange={(e) => setMappingForm({ ...mappingForm, gateId: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-aviation-500 focus:ring-1 focus:ring-aviation-500"
                  >
                    <option value="">Select gate...</option>
                    {airport.gates.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}{g.terminal ? ` (${g.terminal})` : ''}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center text-gray-400 px-2">
                  <Link2 className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Parking Stand</label>
                  <select
                    value={mappingForm.parkingStandId}
                    onChange={(e) => setMappingForm({ ...mappingForm, parkingStandId: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-aviation-500 focus:ring-1 focus:ring-aviation-500"
                  >
                    <option value="">Select stand...</option>
                    {airport.parkingStands.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}{s.isRemote ? ' (Remote)' : ''}</option>
                    ))}
                  </select>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={mappingForm.isDefault}
                    onChange={(e) => setMappingForm({ ...mappingForm, isDefault: e.target.checked })}
                    className="rounded border-gray-300 text-aviation-600"
                  />
                  Default
                </label>
                <button
                  onClick={() => {
                    if (!mappingForm.gateId || !mappingForm.parkingStandId) { setFormError('Select both gate and stand'); return; }
                    createMappingMut.mutate(mappingForm);
                  }}
                  disabled={createMappingMut.isPending}
                  className="flex items-center gap-1.5 rounded-lg bg-aviation-600 px-4 py-2 text-sm font-medium text-white hover:bg-aviation-700 disabled:opacity-50"
                >
                  {createMappingMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                  Link
                </button>
                <button onClick={resetMappingForm} className="rounded-lg border border-gray-300 p-2 text-gray-500 hover:bg-gray-50">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Mappings Table — grouped by gate */}
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium text-gray-600">Gate</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Terminal</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Parking Stand</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Type</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Default</th>
                  {canEdit && <th className="px-4 py-3 font-medium text-gray-600 w-20">Remove</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {airport.gates.every((g) => g.parkingMappings.length === 0) && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No mappings yet</td></tr>
                )}
                {airport.gates.map((gate) =>
                  gate.parkingMappings.map((m, idx) => (
                    <tr key={m.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        {idx === 0 ? (
                          <span className="font-medium text-gray-900">{gate.name}</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {idx === 0 ? gate.terminal ?? '—' : null}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900">{m.parkingStand.name}</span>
                      </td>
                      <td className="px-4 py-3">
                        {m.parkingStand.isRemote ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-700">
                            <Bus className="h-3 w-3" /> Remote
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                            Contact
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {m.isDefault && (
                          <span className="inline-flex rounded-full bg-aviation-50 px-2 py-0.5 text-xs font-medium text-aviation-700">
                            Default
                          </span>
                        )}
                      </td>
                      {canEdit && (
                        <td className="px-4 py-3">
                          <button
                            onClick={() => deleteMappingMut.mutate(m.id)}
                            className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                            title="Remove mapping"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm font-semibold text-gray-900">{value}</dd>
    </div>
  );
}

function GateRow({
  gate,
  canEdit,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  onDeleteMapping,
}: {
  gate: Gate;
  canEdit: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onDeleteMapping: (id: string) => void;
}) {
  return (
    <>
      <tr className="hover:bg-gray-50 cursor-pointer" onClick={onToggle}>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {gate.parkingMappings.length > 0 ? (
              isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />
            ) : <div className="w-4" />}
            <span className="font-medium text-gray-900">{gate.name}</span>
          </div>
        </td>
        <td className="px-4 py-3 text-gray-600">{gate.terminal ?? '—'}</td>
        <td className="px-4 py-3">
          <div className="flex flex-wrap gap-1">
            {gate.parkingMappings.map((m) => (
              <span
                key={m.id}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                  m.parkingStand.isRemote
                    ? 'bg-orange-50 text-orange-700'
                    : 'bg-gray-100 text-gray-700'
                } ${m.isDefault ? 'ring-1 ring-aviation-300' : ''}`}
              >
                {m.parkingStand.name}
                {m.isDefault && <span className="text-aviation-600">*</span>}
              </span>
            ))}
            {gate.parkingMappings.length === 0 && <span className="text-gray-400 text-xs">No stands mapped</span>}
          </div>
        </td>
        <td className="px-4 py-3">
          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
            gate.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {gate.isActive ? 'Active' : 'Inactive'}
          </span>
        </td>
        {canEdit && (
          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex gap-1">
              <button onClick={onEdit} className="rounded p-1 text-gray-400 hover:bg-aviation-50 hover:text-aviation-600" title="Edit">
                <Edit3 className="h-4 w-4" />
              </button>
              <button onClick={onDelete} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600" title="Delete">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </td>
        )}
      </tr>
      {isExpanded && gate.parkingMappings.length > 0 && (
        <tr>
          <td colSpan={5} className="bg-gray-50 px-8 py-3">
            <p className="mb-2 text-xs font-medium text-gray-500">Mapped Parking Stands</p>
            <div className="space-y-1">
              {gate.parkingMappings.map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-lg bg-white px-3 py-2 border border-gray-100">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">{m.parkingStand.name}</span>
                    {m.parkingStand.isRemote && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2 py-0.5 text-xs text-orange-600">
                        <Bus className="h-3 w-3" /> Remote
                      </span>
                    )}
                    {m.isDefault && (
                      <span className="rounded-full bg-aviation-50 px-2 py-0.5 text-xs text-aviation-600">Default</span>
                    )}
                  </div>
                  {canEdit && (
                    <button onClick={() => onDeleteMapping(m.id)} className="rounded p-1 text-gray-400 hover:text-red-500" title="Remove mapping">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function StandRow({
  stand,
  canEdit,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  onDeleteMapping,
}: {
  stand: ParkingStand;
  canEdit: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onDeleteMapping: (id: string) => void;
}) {
  return (
    <>
      <tr className="hover:bg-gray-50 cursor-pointer" onClick={onToggle}>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {stand.gateMappings.length > 0 ? (
              isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />
            ) : <div className="w-4" />}
            <span className="font-medium text-gray-900">{stand.name}</span>
          </div>
        </td>
        <td className="px-4 py-3">
          {stand.isRemote ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-700">
              <Bus className="h-3 w-3" /> Remote
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
              Contact
            </span>
          )}
        </td>
        <td className="px-4 py-3">
          <div className="flex flex-wrap gap-1">
            {stand.gateMappings.map((m) => (
              <span key={m.id} className={`inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 ${m.isDefault ? 'ring-1 ring-aviation-300' : ''}`}>
                {m.gate.name}{m.isDefault ? '*' : ''}
              </span>
            ))}
            {stand.gateMappings.length === 0 && <span className="text-gray-400 text-xs">No gates mapped</span>}
          </div>
        </td>
        <td className="px-4 py-3">
          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
            stand.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {stand.isActive ? 'Active' : 'Inactive'}
          </span>
        </td>
        {canEdit && (
          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex gap-1">
              <button onClick={onEdit} className="rounded p-1 text-gray-400 hover:bg-aviation-50 hover:text-aviation-600" title="Edit">
                <Edit3 className="h-4 w-4" />
              </button>
              <button onClick={onDelete} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600" title="Delete">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </td>
        )}
      </tr>
      {isExpanded && stand.gateMappings.length > 0 && (
        <tr>
          <td colSpan={5} className="bg-gray-50 px-8 py-3">
            <p className="mb-2 text-xs font-medium text-gray-500">Mapped Gates</p>
            <div className="space-y-1">
              {stand.gateMappings.map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-lg bg-white px-3 py-2 border border-gray-100">
                  <div className="flex items-center gap-2">
                    <PlaneTakeoff className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">{m.gate.name}</span>
                    {m.gate.terminal && (
                      <span className="text-xs text-gray-500">({m.gate.terminal})</span>
                    )}
                    {m.isDefault && (
                      <span className="rounded-full bg-aviation-50 px-2 py-0.5 text-xs text-aviation-600">Default</span>
                    )}
                  </div>
                  {canEdit && (
                    <button onClick={() => onDeleteMapping(m.id)} className="rounded p-1 text-gray-400 hover:text-red-500" title="Remove mapping">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
